/* DresdenZuhause — theme.js
 * Lightweight, framework-free. Modules attach themselves on DOMContentLoaded.
 *
 * Public API: window.DZ
 *   - DZ.bus       — EventTarget for cross-module messaging
 *   - DZ.formatMoney(cents) — de-DE / active currency formatter
 *   - DZ.refreshCartCount()
 *   - DZ.openCart()
 */
(() => {
  'use strict';

  /* ==================================================================== */
  /*  Core                                                                */
  /* ==================================================================== */
  const root = document.documentElement;
  if (!root.classList.contains('js')) root.classList.add('js');

  const bus = new EventTarget();
  const DZ = (window.DZ = window.DZ || {});
  DZ.bus = bus;

  const moneyDE = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency:
      window.Shopify && window.Shopify.currency && window.Shopify.currency.active
        ? window.Shopify.currency.active
        : 'EUR',
  });
  DZ.formatMoney = (cents) => moneyDE.format((cents || 0) / 100);

  async function refreshCartCount() {
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const data = await r.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = data.item_count;
        el.hidden = data.item_count === 0;
      });
      bus.dispatchEvent(new CustomEvent('dz:cart:updated', { detail: data }));
      return data;
    } catch (_) {
      /* ignore */
    }
  }
  DZ.refreshCartCount = refreshCartCount;

  /* ==================================================================== */
  /*  Quantity stepper                                                    */
  /* ==================================================================== */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const stepper = btn.closest('.qty-stepper');
    if (!stepper) return;
    const input = stepper.querySelector('input[type="number"]');
    if (!input) return;
    const delta = parseInt(btn.dataset.qtyStep, 10) || 0;
    const min = parseInt(input.min || '1', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || 0) + delta);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ==================================================================== */
  /*  Variant picker                                                      */
  /* ==================================================================== */
  function initProductSection(section) {
    const json = section.querySelector('[data-product-json]');
    if (!json) return;
    let product;
    try {
      product = JSON.parse(json.textContent);
    } catch (_) {
      return;
    }
    const variants = product.variants || [];

    /* Gallery thumb clicks need to work regardless of whether the
       product has variant options — bind first, then bail out of the
       variant-picker setup if there are no [data-option-input]s. */
    const gallery = section.querySelector('[data-gallery]');
    if (gallery) {
      gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest('.gallery__thumb');
        if (!thumb) return;
        const id = thumb.dataset.imageId;
        gallery.querySelectorAll('.gallery__slide').forEach((slide) => {
          slide.classList.toggle('is-active', String(slide.dataset.imageId) === String(id));
        });
        gallery.querySelectorAll('.gallery__thumb').forEach((t) => {
          t.classList.toggle('is-active', t === thumb);
        });
      });
    }

    const inputs = section.querySelectorAll('[data-option-input]');
    if (!inputs.length) return;

    const idInput = section.querySelector('[data-product-form-id]');
    const pricingTarget = section.querySelector('[data-pricing-target]');
    const buyLabel = section.querySelector('[data-buy-label]');
    const addBtn = section.querySelector('[data-add-to-cart]');

    function getSelected() {
      const selected = [];
      inputs.forEach((input) => {
        const idx = parseInt(input.dataset.optionIndex, 10);
        if (input.tagName === 'SELECT') {
          selected[idx] = input.value;
        } else if (input.checked) {
          selected[idx] = input.value;
        }
      });
      return selected;
    }

    function findVariant(selected) {
      return variants.find((v) =>
        (v.options || []).every((val, idx) => val === selected[idx]),
      );
    }

    function format(cents) {
      return DZ.formatMoney(cents);
    }

    function renderPrice(variant) {
      if (!pricingTarget || !variant) return;
      const price = variant.price;
      const compare = variant.compare_at_price && variant.compare_at_price > price ? variant.compare_at_price : null;
      // Lightweight in-place update — replace the .pricing__main and .pricing__compare contents
      const main = pricingTarget.querySelector('.pricing__main');
      const compareEl = pricingTarget.querySelector('.pricing__compare');
      const saveEl = pricingTarget.querySelector('.pricing__save');
      if (main) main.textContent = format(price);
      if (compareEl) {
        if (compare) {
          compareEl.textContent = format(compare);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (saveEl) {
        if (compare) {
          const saveText = saveEl.dataset.template
            ? saveEl.dataset.template.replace('{{ amount }}', format(compare - price))
            : `Sie sparen ${format(compare - price)}`;
          saveEl.textContent = saveText;
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
    }

    function renderBuy(variant) {
      if (!addBtn) return;
      if (!variant) {
        addBtn.disabled = true;
        if (buyLabel) buyLabel.textContent = 'Nicht verfügbar';
        return;
      }
      if (!variant.available) {
        addBtn.disabled = true;
        if (buyLabel) buyLabel.textContent = 'Ausverkauft';
        return;
      }
      addBtn.disabled = false;
      if (buyLabel) buyLabel.textContent = 'In den Warenkorb';
    }

    function renderUrl(variant) {
      if (!variant) return;
      const params = new URLSearchParams(window.location.search);
      params.set('variant', variant.id);
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }

    function renderGallery(variant) {
      if (!gallery || !variant || !variant.featured_image) return;
      const targetId = variant.featured_image.id;
      gallery.querySelectorAll('.gallery__slide').forEach((slide) => {
        slide.classList.toggle('is-active', String(slide.dataset.imageId) === String(targetId));
      });
      gallery.querySelectorAll('.gallery__thumb').forEach((thumb) => {
        thumb.classList.toggle('is-active', String(thumb.dataset.imageId) === String(targetId));
      });
    }

    function update() {
      const selected = getSelected();
      const variant = findVariant(selected);
      if (idInput && variant) idInput.value = variant.id;

      // Sync legend "selected" labels
      section.querySelectorAll('.variant-picker__option').forEach((fs, i) => {
        const lbl = fs.querySelector('[data-option-selected]');
        if (lbl && selected[i]) lbl.textContent = `— ${selected[i]}`;
      });

      renderPrice(variant);
      renderBuy(variant);
      renderUrl(variant);
      renderGallery(variant);
      bus.dispatchEvent(new CustomEvent('dz:variant:changed', { detail: { product, variant } }));
    }

    inputs.forEach((input) => input.addEventListener('change', update));

    /* Gallery thumb clicks already bound above (before the inputs
       early-return), so no duplicate handler here. */
  }

  document.querySelectorAll('[data-product-section]').forEach(initProductSection);

  /* ==================================================================== */
  /*  Age gate                                                            */
  /* ==================================================================== */
  document.querySelectorAll('[data-age-gate-modal]').forEach((dialog) => {
    const section = dialog.closest('[data-product-section]') || document.querySelector('[data-age-gate]');
    if (!section) return;
    const age = section.dataset.ageGate;
    const storageKey = `dz:age_confirmed:${age}`;
    if (sessionStorage.getItem(storageKey) === '1') return;

    // Defer to next tick so the page paints first
    requestAnimationFrame(() => {
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });

    dialog.addEventListener('close', () => {
      if (dialog.returnValue === 'confirm') {
        sessionStorage.setItem(storageKey, '1');
      } else {
        // Cancelled — bounce back home
        window.location.href = '/';
      }
    });
  });

  /* ==================================================================== */
  /*  Cart drawer                                                         */
  /* ==================================================================== */
  const drawer = document.querySelector('[data-cart-drawer]');
  let lastFocus = null;

  function openCart() {
    if (!drawer) {
      window.location.href = '/cart';
      return;
    }
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    const focusable = drawer.querySelector('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) focusable.focus();
    bus.dispatchEvent(new CustomEvent('dz:cart:opened'));
  }
  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    bus.dispatchEvent(new CustomEvent('dz:cart:closed'));
  }
  DZ.openCart = openCart;
  DZ.closeCart = closeCart;

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openCart();
    } else if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeCart();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
      closeCart();
    }
  });

  // If header cart icon links to /cart but a drawer exists, hijack to open it
  if (drawer) {
    document.querySelectorAll('a[href$="/cart"]').forEach((a) => {
      a.addEventListener('click', (e) => {
        if (a.closest('[data-cart-form]')) return; // form submit links are different
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        openCart();
      });
    });
  }

  async function refreshCartDrawer() {
    if (!drawer) return;
    try {
      const r = await fetch('/?section_id=cart-drawer');
      if (!r.ok) return;
      const html = await r.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-body]');
      const target = drawer.querySelector('[data-cart-drawer-body]');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
    } catch (_) {
      /* ignore */
    }
  }
  DZ.refreshCartDrawer = refreshCartDrawer;

  /* ==================================================================== */
  /*  Add to cart (AJAX with drawer; otherwise native post)              */
  /* ==================================================================== */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form#product-form')) return;
    if (!drawer) return; // let native submit happen

    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'add failed');
      await refreshCartCount();
      await refreshCartDrawer();
      bus.dispatchEvent(new CustomEvent('dz:cart:added', { detail: data }));
      // Marigold petal joy moment — quick rotate-and-fade above the cart icon
      document.querySelectorAll('[data-cart-icon]').forEach((el) => {
        el.classList.remove('is-blooming');
        // force reflow so the animation restarts cleanly when adds repeat
        void el.offsetWidth;
        el.classList.add('is-blooming');
        setTimeout(() => el.classList.remove('is-blooming'), 800);
      });
      openCart();
    } catch (err) {
      alert(err.message || 'Konnte nicht in den Warenkorb gelegt werden.');
    } finally {
      if (submitBtn) submitBtn.removeAttribute('aria-busy');
    }
  });

  /* ==================================================================== */
  /*  Cart line: change quantity / remove (works both in drawer and page) */
  /* ==================================================================== */
  let qtyDebounce;
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    const line = input.closest('[data-line-key]');
    if (!line) return;
    const key = line.dataset.lineKey;
    const qty = parseInt(input.value, 10) || 0;
    clearTimeout(qtyDebounce);
    qtyDebounce = setTimeout(() => updateLine(key, qty), 200);
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    const line = remove.closest('[data-line-key]');
    if (!line) return;
    await updateLine(line.dataset.lineKey, 0);
  });

  async function updateLine(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'cart update failed');

      // Refresh drawer + count + page if we're on the cart page
      await refreshCartCount();
      if (drawer && drawer.classList.contains('is-open')) await refreshCartDrawer();

      // If we're on the cart page, refresh the form area for accurate totals
      const cartPageForm = document.getElementById('cart-page-form');
      if (cartPageForm) {
        const r = await fetch('/cart');
        if (r.ok) {
          const html = await r.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.getElementById('cart-page-form');
          if (fresh) cartPageForm.replaceWith(fresh);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  /* ==================================================================== */
  /*  Product recommendations (lazy load)                                 */
  /* ==================================================================== */
  document.querySelectorAll('[data-product-recommendations]').forEach((el) => {
    const url = el.dataset.url;
    if (!url) return;
    const io = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.disconnect();
          try {
            const r = await fetch(url);
            if (!r.ok) return;
            const html = await r.text();
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const fresh = parsed.querySelector('[data-product-recommendations]');
            if (fresh) el.innerHTML = fresh.innerHTML;
          } catch (_) {
            /* ignore */
          }
        }
      },
      { rootMargin: '600px 0px' },
    );
    io.observe(el);
  });

  /* ==================================================================== */
  /*  Copy-link / share                                                   */
  /* ==================================================================== */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-link]');
    if (!btn) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(btn.dataset.copyLink || location.href);
      const original = btn.textContent;
      btn.textContent = btn.dataset.copiedLabel || 'Link kopiert';
      setTimeout(() => {
        btn.textContent = original;
      }, 1600);
    } catch (_) {
      /* ignore */
    }
  });

  /* ==================================================================== */
  /*  PDP tabs                                                             */
  /* ==================================================================== */
  /* Click / arrow-key tab switcher. Without JS, all panels stay visible
     because the .is-hidden class only kicks in once we initialise. */
  document.querySelectorAll('[data-product-tabs]').forEach((root) => {
    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
    if (!tabs.length || !panels.length) return;

    function activate(tab) {
      tabs.forEach((t) => {
        const active = t === tab;
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.classList.toggle('is-active', active);
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach((p) => {
        const isFor = p.getAttribute('aria-labelledby') === tab.id;
        p.hidden = !isFor;
        p.classList.toggle('is-hidden', !isFor);
      });
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          tabs[next].focus();
          activate(tabs[next]);
        }
      });
    });
  });

  /* ==================================================================== */
  /*  Lazy image fade-in                                                  */
  /* ==================================================================== */
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (img.complete) img.classList.add('is-loaded');
    else img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
  });

  /* ==================================================================== */
  /*  Predictive search                                                   */
  /* ==================================================================== */
  /*
   * Inline live-search dropdown. Uses Shopify's /search/suggest.json to
   * return products + collections + pages as the user types. Keyboard
   * navigation (↑/↓/Enter/Escape) is fully supported. The dropdown
   * always has a visible "Alle Ergebnisse anzeigen" link that submits
   * the form to /search?q=… for the full results page.
   */
  document.querySelectorAll('[data-predictive]').forEach((root) => {
    const toggle = root.querySelector('[data-predictive-toggle]') || root.querySelector('[data-predictive-input]');
    const panel = root.querySelector('[data-predictive-panel]');
    const form = root.querySelector('[data-predictive-form]');
    const input = root.querySelector('[data-predictive-input]');
    const results = root.querySelector('[data-predictive-results]');
    const seeAll = root.querySelector('[data-predictive-see-all]');
    const closeBtn = root.querySelector('[data-predictive-close]');
    if (!panel || !input || !results) return;

    let abortCtrl = null;
    let debounceId = null;
    let lastQuery = '';
    let activeIndex = -1;

    const escapeHtml = (s) =>
      String(s || '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      })[c]);

    function open() {
      panel.hidden = false;
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }
    function close() {
      panel.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
      if (abortCtrl) { abortCtrl.abort(); abortCtrl = null; }
    }

    function getHits() {
      return Array.from(results.querySelectorAll('[data-suggest-hit]'));
    }
    function setActive(idx) {
      const hits = getHits();
      if (!hits.length) { activeIndex = -1; return; }
      activeIndex = ((idx % hits.length) + hits.length) % hits.length;
      hits.forEach((h, i) => h.classList.toggle('is-active', i === activeIndex));
      hits[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    function renderEmpty() {
      results.innerHTML = '';
    }
    function renderHint() {
      results.innerHTML = '<p class="predictive__hint">Tippen Sie, um zu suchen.</p>';
    }
    function renderNoResults(q) {
      results.innerHTML =
        '<p class="predictive__hint">Keine Treffer für „' + escapeHtml(q) + '".</p>';
    }
    function renderResults(data, q) {
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
      const pages = (data.resources && data.resources.results && data.resources.results.pages) || [];

      if (!products.length && !collections.length && !pages.length) {
        renderNoResults(q);
        return;
      }

      const parts = [];
      if (products.length) {
        parts.push('<h3 class="predictive__group">Produkte</h3>');
        parts.push('<ul class="predictive__list" role="listbox">');
        products.forEach((p) => {
          const img = p.featured_image && p.featured_image.url
            ? '<img src="' + escapeHtml(p.featured_image.url) + '" alt="" loading="lazy" width="56" height="56">'
            : '<span class="predictive__thumb-placeholder" aria-hidden="true"></span>';
          const price = typeof p.price === 'number'
            ? DZ.formatMoney(p.price * 100)
            : (p.price ? p.price : '');
          parts.push(
            '<li><a class="predictive__hit" data-suggest-hit href="' + escapeHtml(p.url) + '" role="option">' +
              '<span class="predictive__thumb">' + img + '</span>' +
              '<span class="predictive__body">' +
                '<span class="predictive__title">' + escapeHtml(p.title) + '</span>' +
                (p.vendor ? '<span class="predictive__meta">' + escapeHtml(p.vendor) + '</span>' : '') +
              '</span>' +
              (price ? '<span class="predictive__price">' + escapeHtml(price) + '</span>' : '') +
            '</a></li>',
          );
        });
        parts.push('</ul>');
      }
      if (collections.length) {
        parts.push('<h3 class="predictive__group">Kategorien</h3>');
        parts.push('<ul class="predictive__list" role="listbox">');
        collections.forEach((c) => {
          parts.push(
            '<li><a class="predictive__hit predictive__hit--text" data-suggest-hit href="' + escapeHtml(c.url) + '" role="option">' +
              '<span class="predictive__title">' + escapeHtml(c.title) + '</span>' +
            '</a></li>',
          );
        });
        parts.push('</ul>');
      }
      if (pages.length) {
        parts.push('<h3 class="predictive__group">Seiten</h3>');
        parts.push('<ul class="predictive__list" role="listbox">');
        pages.forEach((pg) => {
          parts.push(
            '<li><a class="predictive__hit predictive__hit--text" data-suggest-hit href="' + escapeHtml(pg.url) + '" role="option">' +
              '<span class="predictive__title">' + escapeHtml(pg.title) + '</span>' +
            '</a></li>',
          );
        });
        parts.push('</ul>');
      }
      results.innerHTML = parts.join('');
    }

    async function search(q) {
      if (!q || q.length < 2) { renderHint(); return; }
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();
      const url =
        '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page' +
        '&resources[limit]=8&resources[options][unavailable_products]=last';
      try {
        const res = await fetch(url, { signal: abortCtrl.signal, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('suggest ' + res.status);
        const data = await res.json();
        if (q !== lastQuery) return; /* dropped — newer request in flight */
        renderResults(data, q);
      } catch (err) {
        if (err.name === 'AbortError') return;
        renderEmpty();
      }
    }

    if (toggle && toggle !== input) {
      toggle.addEventListener('click', () => {
        if (panel.hidden) { open(); renderHint(); requestAnimationFrame(() => input.focus()); }
        else close();
      });
    }
    if (closeBtn) closeBtn.addEventListener('click', () => close());

    input.addEventListener('focus', () => {
      if (panel.hidden) { open(); if (!input.value.trim()) renderHint(); }
    });

    input.addEventListener('input', () => {
      const q = input.value.trim();
      lastQuery = q;
      if (seeAll) seeAll.href = q ? '/search?q=' + encodeURIComponent(q) : '/search';
      if (panel.hidden) open();
      clearTimeout(debounceId);
      debounceId = setTimeout(() => search(q), 180);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); toggle.focus(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); return; }
      if (e.key === 'Enter') {
        const hits = getHits();
        if (activeIndex >= 0 && hits[activeIndex]) {
          e.preventDefault();
          window.location = hits[activeIndex].href;
        } /* else: form submits naturally to /search?q=… */
      }
    });

    if (form) {
      form.addEventListener('submit', () => { /* let it submit */ });
    }

    document.addEventListener('click', (e) => {
      if (panel.hidden) return;
      if (root.contains(e.target)) return;
      close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !panel.hidden) close();
    });
  });
})();
